import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { LocalStorageKeys } from '@/utils/local-storage';
import { LocalStorage } from 'quasar';
import { useAuthStore } from '@/store/authStore';
import { useGeneralStore } from '@/store/generalStore';
import { usePluginsStore } from '@/store/pluginsStore';

export enum OrganizationPreferencesRoutes {
  OrganizationProfile = 'app:organization:profile',
  OrganizationSubscription = 'app:organization:subscription',
  OrganizationIntegrations = 'app:organization:integrations',
  OrganizationImport = 'app:organization:import',
  OrganizationUsers = 'app:organization:users',
  OrganizationTeams = 'app:organization:teams',
  OrganizationSettings = 'app:organization:settings',
  OrganizationManagement = 'app:organization:management',
  SubscriptionProducts = 'app:organization:subscription:products',
  JobHistory = 'app:organization:job-history',
  OrganizationPermissions = 'app:organization:permissions',
  OrganizationForms = 'app:organization:forms',
  OrganizationNotifications = 'app:organization:notifications',
  TemplateImportJobHistory = 'app:organization:template-import-job-history',
  ThemeEditor = 'app:organization:theme-editor',
  OrganizationDatabaseAccess = 'app:organization:database-access',
}

export enum AccountRoutes {
  AccountProfile = 'app:my-account:profile',
  AccountOrganizations = 'app:my-account:organizations',
  AccountMultiFactorAuth = 'app:my-account:multi-factor-auth',
  AccountNotifications = 'app:my-account:account-notifications',
  AccountApiKeys = 'app:my-account:api-keys',
}

export enum MainRouteNames {
  AuthenticationLogin = 'authentication:login',
  SignupMain = 'signup:main',
  Map = 'app:map',
  Items = 'app:items',

  Boards = 'app:boards',
  Board = 'app:board',
  BoardIndex = 'app:boards:index',
  BoardFolder = 'app:boards:folder',

  Gantt = 'app:plan:gantt',
  Work = 'app:my-work',
  Analyze = 'app:data',
  MyAccount = 'settings:account',
  Organization = 'app:organization',
  Notifications = 'notifications',
  Item = 'app:item',
  Automate = 'app:automate',
  PrivacyPolicy = 'privacy-policy',
  TermsOfUse = 'terms-of-use',
  OauthRedirect = 'oauth-redirect',
  Plugin = 'plugin',
}

export enum PublicRouteNames {
  ItemRequest = 'public:item-request',
  StoredView = 'public:stored-view',
}

export const RouteNames = {
  ...MainRouteNames,
  ...OrganizationPreferencesRoutes,
  ...AccountRoutes,
  ...PublicRouteNames,
};

const routes: RouteRecordRaw[] = [
  {
    path: '/login/',
    name: RouteNames.AuthenticationLogin, // Do not change this because it is used by the router guard
    component: () => import('@/pages/authorization/AppLogin.vue'),
  },
  {
    path: '/signup/',
    name: RouteNames.SignupMain, // Do not change this because it is used by the router guard
    component: () => import('@/pages/signup/AppSignup.vue'),
  },
  {
    path: '/:organization?',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        redirect: { name: RouteNames.Map },
      },
      {
        name: RouteNames.Item,
        path: 'items/:itemNumber',
        redirect: (to) => ({
          name: RouteNames.Map,
          query: {
            ...(to.query || {}),
            itemNumber: to.params.itemNumber,
          },
        }),
      },
      {
        path: 'map',
        name: RouteNames.Map,
        component: () => import('@/pages/map/MapView.vue'),
      },
      {
        path: 'items',
        name: RouteNames.Items,
        component: () => import('@/pages/items/ItemsMainView.vue'),
      },
      {
        path: 'boards',
        name: RouteNames.Boards,
        redirect: { name: RouteNames.BoardIndex },
        children: [
          {
            path: '',
            name: RouteNames.BoardIndex,
            component: () => import('@/pages/plan/BoardFoldersView.vue'),
            // TODO: figure out a way to type this if this pattern becomes common
            meta: {
              layout: {
                useContentScroll: true,
              },
            },
          },
          {
            path: 'f/:folderIdentifier',
            name: RouteNames.BoardFolder,
            component: () => import('@/pages/plan/BoardFoldersView.vue'),
            meta: {
              layout: {
                useContentScroll: true,
              },
            },
          },
          {
            path: 'b/:boardIdentifier',
            name: RouteNames.Board,
            component: () =>
              import('@/pages/plan/components/boards/BoardView.vue'),
          },
        ],
      },
      {
        path: 'gantt',
        name: RouteNames.Gantt,
        component: () => import('@/pages/plan/PlanView.vue'),
        props: {
          activeTab: 'gantt',
        },
      },
      {
        path: 'work',
        name: RouteNames.Work,
        component: () => import('@/pages/work/WorkView.vue'),
      },
      {
        path: 'analyze',
        name: RouteNames.Analyze,
        component: () => import('@/pages/analyze/AnalyzeView.vue'),
      },
      {
        path: 'automate',
        name: RouteNames.Automate,
        component: () => import('@/pages/automate/AutomateView.vue'),
      },
      {
        path: 'my-account',
        name: RouteNames.MyAccount,
        component: () => import('@/pages/settings/MyAccount.vue'),
        redirect: { name: 'app:my-account:profile' },
        children: [
          {
            path: 'profile',
            name: RouteNames.AccountProfile,
            component: () => import('@/pages/settings/account/UserProfile.vue'),
          },
          {
            path: 'organizations',
            name: RouteNames.AccountOrganizations,
            component: () =>
              import('@/pages/settings/account/UserOrganizations.vue'),
          },
          {
            path: 'multi-factor-auth',
            name: RouteNames.AccountMultiFactorAuth,
            component: () =>
              import('@/pages/settings/account/MultiFactorAuth.vue'),
          },
          {
            path: 'notifications',
            name: RouteNames.AccountNotifications,
            component: () =>
              import('@/pages/settings/account/AppNotifications.vue'),
          },
          {
            path: 'api-keys',
            name: RouteNames.AccountApiKeys,
            component: () => import('@/pages/settings/account/ApiKeys.vue'),
          },
        ],
      },
      {
        path: 'notifications',
        name: RouteNames.Notifications,
        component: () => import('@/pages/notifications/NotificationsView.vue'),
      },
      {
        path: 'plugins/:pluginId',
        name: RouteNames.Plugin,
        component: () => import('@/pages/plugin/PluginView.vue'),
        // Layout opts similar to other heavy views
        meta: {
          layout: {
            useContentScroll: true,
          },
        },
      },
      {
        path: 'organization',
        name: RouteNames.Organization,
        component: () => import('@/pages/settings/OrganizationSettings.vue'),
        redirect: { name: 'app:organization:profile' },
        children: [
          {
            path: 'profile',
            name: RouteNames.OrganizationProfile,
            component: () => import('@/pages/settings/UserProfile.vue'),
          },
          {
            path: 'users',
            name: RouteNames.OrganizationUsers,
            component: () => import('@/pages/settings/OrganizationUsers.vue'),
          },
          {
            path: 'teams',
            name: RouteNames.OrganizationTeams,
            component: () => import('@/pages/settings/OrganizationTeams.vue'),
          },
          {
            path: 'permissions',
            name: RouteNames.OrganizationPermissions,
            component: () =>
              import('@/pages/settings/OrganizationPermissions.vue'),
          },
          {
            path: 'database-access',
            name: RouteNames.OrganizationDatabaseAccess,
            component: () =>
              import('@/pages/settings/OrganizationDatabaseAccess.vue'),
          },
          {
            path: 'subscription',
            name: RouteNames.OrganizationSubscription,
            component: () =>
              import('@/pages/settings/SubscriptionSettings.vue'),
          },
          {
            path: 'integrations',
            name: RouteNames.OrganizationIntegrations,
            component: () => import('@/pages/settings/ModuleIntegrations.vue'),
          },
          {
            path: 'settings',
            name: RouteNames.OrganizationSettings,
            component: () => import('@/pages/settings/AppSettings.vue'),
          },
          {
            path: 'job-history',
            name: RouteNames.JobHistory,
            component: () => import('@/pages/settings/JobHistory.vue'),
          },
          {
            path: 'template-import-job-history',
            name: RouteNames.TemplateImportJobHistory,
            component: () =>
              import('@/pages/settings/ImportTemplateJobHistory.vue'),
          },
          {
            path: 'subscription-products',
            name: RouteNames.SubscriptionProducts,
            component: () =>
              import('@/pages/settings/SubscriptionProducts.vue'),
          },
          {
            path: 'organization-management',
            name: RouteNames.OrganizationManagement,
            component: () =>
              import('@/pages/settings/OrganizationManagement.vue'),
          },
          {
            path: 'theme-editor',
            name: RouteNames.ThemeEditor,
            component: () => import('@/pages/settings/ThemeEditor.vue'),
          },
          {
            path: 'forms',
            name: RouteNames.OrganizationForms,
            component: () => import('@/pages/settings/OrganizationForms.vue'),
          },
          {
            path: 'notifications',
            name: RouteNames.OrganizationNotifications,
            component: () =>
              import('@/pages/settings/OrganizationNotifications.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/item-request/:formMetadata',
    name: PublicRouteNames.ItemRequest,
    component: () => import('@/pages/item-request/ItemRequestForm.vue'),
  },
  {
    path: '/stored-view/:formMetadata',
    name: PublicRouteNames.StoredView,
    component: () => import('@/pages/stored-view/StoredViewList.vue'),
  },
  {
    path: '/privacy-policy',
    name: RouteNames.PrivacyPolicy,
    component: () => import('@/pages/agreements/PrivacyPolicy.vue'),
  },
  {
    path: '/terms-of-use',
    name: RouteNames.TermsOfUse,
    component: () => import('@/pages/agreements/TermsOfUse.vue'),
  },
  {
    path: '/:organization/oauth',
    name: RouteNames.OauthRedirect,
    component: () => import('@/pages/OauthRedirect.vue'),
  },
  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:pathMatch(.*)*',
    component: () => import('@/pages/ErrorCode404.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  const generalStore = useGeneralStore();
  generalStore.setIsNavigating(true);

  // Strip unsafe query params for Plugin route
  if (to.name === RouteNames.Plugin) {
    const { url, name, ...rest } = to.query || {};
    if (url || name) {
      next({
        name: RouteNames.Plugin,
        params: to.params,
        query: rest,
        replace: true,
      });
      return;
    }
  }

  if (authStore.isLoggedIn) {
    if (
      to.query['integrations'] &&
      to.query['setup_action'] === 'install' &&
      to.name !== RouteNames.OrganizationIntegrations
    ) {
      next({
        name: RouteNames.OrganizationIntegrations,
        query: to.query,
      });
      return;
    }
    to.name !== MainRouteNames.AuthenticationLogin
      ? next()
      : next({ path: '/' });
    return;
  } else if (
    to.name === MainRouteNames.AuthenticationLogin ||
    to.name === MainRouteNames.SignupMain ||
    to.name === MainRouteNames.PrivacyPolicy ||
    to.name === MainRouteNames.TermsOfUse ||
    to.name === PublicRouteNames.ItemRequest ||
    to.name === PublicRouteNames.StoredView
  ) {
    next();
    return;
  }
  if (!to.fullPath.includes('login') && !to.fullPath.includes('my-account')) {
    // if we are not going to the login page and we didn't come from the page that has the logout button:
    LocalStorage.set(LocalStorageKeys.REDIRECT_URL, to.fullPath);
  }
  // Clear plugin caches when navigating to login (likely after logout)
  try {
    const pluginsStore = usePluginsStore();
    pluginsStore.clearAll();
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[Router] Failed to clear plugins store:', err);
    }
  }
  next({ name: MainRouteNames.AuthenticationLogin });
  // If we want to support role based authentication we can use something like
  // https://github.com/stefanvanherwijnen/quasar-auth-starter/blob/master/frontend/@/boot/auth.js#L128
});
router.afterEach(() => {
  const generalStore = useGeneralStore();
  generalStore.setIsNavigating(false);
});

// If someone has the old page open when we deployed and the cache was invalidated
// We need to reload the page so they get a fresh index.html
router.onError((error) => {
  if (/Failed to fetch dynamically imported module/i.test(error.message)) {
    const isDev = import.meta.env.MODE === 'development';
    if (isDev) {
      console.error(error);
    } else {
      window.location.reload();
    }
  }
});

export default router;
